import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { TierProFilled } from '@heidi/design-system/icons';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyP,
  typographyVariant,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Check, Info } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { UpgradeCtaButton } from '../upgrade-cta-button/upgrade-cta-button';
import {
  COMPLIMENTARY_ENDED_PLAN_NAMES,
  type ComplimentaryEndedFeatureList,
  type ComplimentaryEndedTarget,
  type ComplimentaryEndedVariant,
} from './complimentary-ended-variants';
import { complimentaryEndedPlanMessages as planMessages } from './complimentary-ended-variants.messages';

type PlanTab = 'target' | 'current';

export type ComplimentaryTrialEndedModalProps = {
  open: boolean;
  variant: ComplimentaryEndedVariant;
  onUpgrade: (target: ComplimentaryEndedTarget) => void;
  onStay: () => void;
  onDismiss: () => void;
};

const FeatureRow = ({
  icon,
  label,
  tip,
}: {
  icon: ReactNode;
  label: string;
  tip: ReactNode;
}) => {
  const intl = useIntl();

  return (
    <li className="flex items-center gap-2.5 ps-2">
      {icon}
      <span className="flex items-center gap-1">
        <TypographyP className="text-text-primary">{label}</TypographyP>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              iconOnly
              disableAutomaticSvgSizing
              className="size-5 min-w-5 shrink-0 rounded-sm p-0 text-text-secondary hover:text-text-primary"
              aria-label={intl.formatMessage(planMessages.featureInfo, {
                feature: label,
              })}
            >
              <Info aria-hidden className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-start">
            {tip}
            <TooltipArrow />
          </TooltipContent>
        </Tooltip>
      </span>
    </li>
  );
};

const FeatureList = ({ list }: { list: ComplimentaryEndedFeatureList }) => {
  const intl = useIntl();

  return (
    <div className="flex w-full flex-col gap-3 text-start">
      <TypographyP className="text-text-secondary">
        <FormattedMessage {...list.heading} />
      </TypographyP>
      <ul className="flex flex-col gap-3">
        {list.items.map((item) => (
          <FeatureRow
            key={item.label.id}
            icon={
              <Check aria-hidden className="size-[18px] shrink-0 text-text-primary" />
            }
            label={intl.formatMessage(item.label)}
            tip={<FormattedMessage {...item.tip} />}
          />
        ))}
      </ul>
    </div>
  );
};

/**
 * Shown once complimentary usage ends. The variant sets the copy, plan sold,
 * preselected tab and leading action; the host owns queue, analytics, routing.
 */
export const ComplimentaryTrialEndedModal = ({
  open,
  variant,
  onUpgrade,
  onStay,
  onDismiss,
}: ComplimentaryTrialEndedModalProps) => {
  const intl = useIntl();
  const [tab, setTab] = useState<PlanTab>(variant.isRecommended ? 'target' : 'current');

  if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return null;

  const currentPlanName = intl.formatMessage(
    COMPLIMENTARY_ENDED_PLAN_NAMES[variant.currentPlan]
  );
  const stayLabel = (
    <FormattedMessage {...planMessages.stayOn} values={{ planName: currentPlanName }} />
  );
  const upgradeLabel = <FormattedMessage {...variant.upgradeLabel} />;
  const handleUpgrade = () => onUpgrade(variant.target);
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onDismiss();
  };

  return (
    <DialogDrawer open={open} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        className="flex flex-col items-center gap-6 rounded-2xl p-8 text-center sm:max-w-[470px]"
        data-testid="complimentary-trial-ended-modal"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex w-full flex-col items-center gap-4">
          <DialogDrawerTitle
            className={cn(typographyVariant({ variant: 'h2' }), 'text-text-primary')}
          >
            <FormattedMessage {...variant.title} />
          </DialogDrawerTitle>
          <DialogDrawerDescription
            className={cn(typographyVariant({ variant: 'p' }), 'text-text-secondary')}
          >
            <FormattedMessage {...variant.body} />
          </DialogDrawerDescription>
        </div>

        <SegmentedControl
          value={tab}
          onValueChange={(next) => setTab(next === 'target' ? 'target' : 'current')}
          className="w-full"
        >
          <SegmentedControlList className="w-full">
            <SegmentedControlTrigger
              value="target"
              className="gap-2"
              data-testid="complimentary-trial-ended-modal-target-tab"
            >
              <FormattedMessage {...COMPLIMENTARY_ENDED_PLAN_NAMES[variant.target]} />
              {variant.isRecommended ? (
                <Badge size="xs" color="green">
                  <FormattedMessage {...planMessages.recommended} />
                </Badge>
              ) : null}
            </SegmentedControlTrigger>
            <SegmentedControlTrigger
              value="current"
              className="gap-2"
              data-testid="complimentary-trial-ended-modal-current-tab"
            >
              {currentPlanName}
              <Badge size="xs" color="neutral">
                <FormattedMessage {...planMessages.current} />
              </Badge>
            </SegmentedControlTrigger>
          </SegmentedControlList>
        </SegmentedControl>

        <TooltipProvider delayDuration={200}>
          <FeatureList
            list={tab === 'target' ? variant.targetFeatures : variant.currentFeatures}
          />
        </TooltipProvider>

        <div className="flex w-full flex-col gap-2">
          {variant.isRecommended ? (
            <>
              {/* Clinician and Max upgrades share the blue Clinician CTA fill. */}
              <UpgradeCtaButton
                tier="clinicianMax"
                size="lg"
                className="w-full"
                label={upgradeLabel}
                onClick={handleUpgrade}
                data-testid="complimentary-trial-ended-modal-upgrade"
              />
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full"
                onClick={onStay}
                data-testid="complimentary-trial-ended-modal-stay"
              >
                {stayLabel}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                size="lg"
                className="w-full"
                onClick={onStay}
                data-testid="complimentary-trial-ended-modal-stay"
              >
                {stayLabel}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full gap-x-1.5"
                onClick={handleUpgrade}
                data-testid="complimentary-trial-ended-modal-upgrade"
              >
                <TierProFilled size={16} className="shrink-0" aria-hidden />
                {upgradeLabel}
              </Button>
            </>
          )}
        </div>

        <TypographyCaption className="text-text-secondary">
          <FormattedMessage {...planMessages.footnote} />
        </TypographyCaption>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
